import React from 'react';
import { Box, Container, Typography, Link } from '@mui/material';
import AnimatedElement from './AnimatedElement';
import { useCms, useSection } from '../context/CmsContext';

/**
 * Zeigt die letzten Instagram-Beiträge. Die Bilder liefert der eigene
 * Server aus - im Browser der Besucher entsteht keine Verbindung zu Meta.
 * Ohne verbundenes Konto erscheint der Abschnitt gar nicht.
 */
const InstagramSection = () => {
  const instagram = useSection('instagram');
  const { instagram: feed } = useCms();
  const items = (feed && feed.items) || [];
  const enabled = feed && feed.status && feed.status.enabled;

  if (!enabled || items.length === 0) {
    return null;
  }

  return (
    <Box
      id="instagram"
      sx={{
        py: { xs: 8, md: 12 },
        bgcolor: '#fff',
      }}
    >
      <Container maxWidth={false} sx={{ maxWidth: '1400px', mx: 'auto', px: 2 }}>

        {/* HEADER */}
        <AnimatedElement>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr auto' },
              alignItems: 'end',
              mb: { xs: 5, md: 8 },
            }}
          >
            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '1.6rem', md: '2.2rem' },
                  fontWeight: 500,
                  color: '#000',
                }}
              >
                {instagram.heading}
              </Typography>

              <Box sx={{ height: '4px', width: '100px', backgroundColor: '#3a7b3e', mt: 2 }} />

              {instagram.intro && (
                <Typography
                  sx={{
                    fontSize: { xs: '1rem', md: '1.05rem' },
                    lineHeight: 1.7,
                    color: '#111',
                    mt: 3,
                    maxWidth: 560,
                  }}
                >
                  {instagram.intro}
                </Typography>
              )}

              {instagram.profileUrl && (
                <Link
                  href={instagram.profileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: 'inline-block',
                    mt: 2,
                    color: '#3a7b3e',
                    fontSize: '0.95rem',
                    textDecoration: 'none',
                    borderBottom: '1px solid #3a7b3e',
                    pb: '2px',
                    transition: '0.25s ease',
                    '&:hover': { opacity: 0.7 },
                  }}
                >
                  {instagram.profileLabel || 'Auf Instagram folgen'}
                </Link>
              )}
            </Box>

            <Typography
              sx={{
                display: { xs: 'none', md: 'block' },
                color: '#111',
                letterSpacing: '0.2em',
                writingMode: 'vertical-rl',
                textOrientation: 'mixed',
                opacity: 0.8,
              }}
            >
              {instagram.label}
            </Typography>
          </Box>
        </AnimatedElement>

        {/* BEITRÄGE */}
        <AnimatedElement>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, 1fr)',
                sm: 'repeat(3, 1fr)',
                md: 'repeat(4, 1fr)',
                lg: 'repeat(6, 1fr)',
              },
              gap: { xs: 1, md: 2 },
            }}
          >
            {items.map((item) => (
              <Link
                key={item.id}
                href={item.permalink}
                target="_blank"
                rel="noopener noreferrer"
                title={item.caption || 'Beitrag auf Instagram ansehen'}
                sx={{
                  display: 'block',
                  position: 'relative',
                  aspectRatio: '1 / 1',
                  overflow: 'hidden',
                  borderRadius: 2,
                  backgroundColor: '#f1f1ed',
                  transition: '0.3s ease',
                  '&:hover': {
                    boxShadow: '0 10px 25px rgba(0,0,0,0.18)',
                  },
                  '&:hover img': {
                    transform: 'scale(1.06)',
                  },
                }}
              >
                <Box
                  component="img"
                  src={item.image}
                  alt={item.caption ? item.caption.slice(0, 120) : 'Instagram-Beitrag'}
                  loading="lazy"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                    transition: 'transform 0.8s ease',
                  }}
                />
              </Link>
            ))}
          </Box>
        </AnimatedElement>

      </Container>
    </Box>
  );
};

export default InstagramSection;
